import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import {
  BASE_URL,
  DASHBOARD_URL,
  FORGOT_PASSWORD_URL,
} from "../../Routes/urls";
import { warningActions } from "../../store/slices/WarningSlice";
import { validateEmail } from "../../utils";
import { LoginData, Tenant } from "../../ts/types";
import { login } from "../../services/login";
import { AppDispatch, RootState } from "../../store";
import { fetchTenantsData } from "../../store/actions/UserActions";
import classes from "./SignIn.module.css";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Card from "../../components/Card/Card";
import logoImage from "../../assets/cimpar-logo.svg";
import Input from "../../components/Inputs/Input";
import LinkButton from "../../components/Buttons/LinkButton";
import TwoFactorAuthentication from "./TwoFactorAuthentication";
import { UserRole } from "../../ts/enums";
import { authActions } from "../../store/slices/AuthSlice";

type Props = {};

const getInitialFieldErrors = () => {
  return {
    email: false,
    password: false,
  };
};

const SignIn: React.FC<Props> = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const resourceName =
    useSelector((state: RootState) => state.auth.resourceName) || "";
  const tenants = useSelector((state: RootState) => state.users.tenants);

  const [userDetails, setUserDetails] = useState<LoginData>({
    email: "",
    password: "",
  });
  const [fieldErrors, setFieldErrors] = useState(getInitialFieldErrors());
  const [showAuthenticationModal, setShowAuthenticationModal] = useState(false);
  const environments = tenants.map((tenant: Tenant) => tenant.id);

  const isFieldsValid = (): boolean => {
    const fieldErrors = getInitialFieldErrors();

    if (!userDetails.email || !validateEmail(userDetails.email)) {
      fieldErrors.email = true;
    }

    if (!userDetails.password) {
      fieldErrors.password = true;
    }

    setFieldErrors(fieldErrors);

    return Object.values(fieldErrors).every((value) => value === false);
  };

  const clearFieldErrors = (key: keyof typeof fieldErrors) => {
    if (fieldErrors[key])
      setFieldErrors((prevState) => {
        const newState = { ...prevState };
        newState[key] = false;
        return newState;
      });
  };

  const signInHandler = async () => {
    if (!isFieldsValid()) {
      return;
    }

    if (!environments.includes(resourceName)) {
      dispatch(
        warningActions.showWarning({
          message: `Invalid tenant name "${resourceName}". Please check the url.`,
        })
      );
      return;
    }

    try {
      const res = await login(userDetails);
      if (res.status === 200) {
        if (res.data.success) {
          if (res.data?.user?.role === UserRole.SuperAdmin) {
            localStorage.setItem("isLoggedIn", "true");
            dispatch(authActions.setAuthenticated());
            navigate(`${BASE_URL}/${resourceName}/${DASHBOARD_URL}`);
          } else {
            setShowAuthenticationModal(true);
          }
        } else {
          if (res.data.errorCode === "access_denied") {
            dispatch(
              warningActions.showWarning({
                message:
                  "Sorry, you do not have the permission to access the Reya portal.",
              })
            );
          } else {
            dispatch(
              warningActions.showWarning({
                message: "Invalid username or password",
              })
            );
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const forgotPasswordHandler = () => {
    navigate(`${BASE_URL}/${resourceName}/${FORGOT_PASSWORD_URL}`);
  };

  useEffect(() => {
    if (tenants?.length === 0) {
      dispatch(fetchTenantsData());
    }
  }, []);

  const getTenant = (key: any) => {
    const tenant = tenants?.find(
      (tenant: Tenant) => tenant.id === resourceName
    );
    if (tenant) {
      if (key === "name") {
        return tenant.name;
      } else if (key === "logoImage") {
        return tenant?.logoImage;
      }
    } else {
      return "";
    }
  };

  return (
    <>
      {showAuthenticationModal && (
        <TwoFactorAuthentication
          onClose={() => setShowAuthenticationModal(false)}
          email={userDetails.email}
        />
      )}
      <div className={classes.main}>
        {tenants?.length ? (
          <div className={classes.tenantContainer}>
            {getTenant("logoImage") ? (
              <div className={classes.logoImage}>
                <img src={getTenant("logoImage")} alt='' />
              </div>
            ) : (
              <Card className={classes.tenantCard}>
                <div>{getTenant("name")}</div>
              </Card>
            )}
          </div>
        ) : (
          <></>
        )}
        <Card className={classes.card}>
          <div className={classes.logoContainer}>
            <img src={logoImage} alt='CIMPAR Logo' className={classes.logo} />
          </div>
          <div className={classes.heading}>Login</div>
          <div className={classes.container}>
            <Input
              name='Email'
              value={userDetails.email}
              onChange={(e) => {
                setUserDetails((prevState) => {
                  return {
                    ...prevState,
                    email: e.target.value,
                  };
                });
                clearFieldErrors("email");
              }}
              isInvalid={fieldErrors.email}
              invalidText='Invalid email'
              className={classes.inputContainer}
              inputClassName={classes.input}
            />
            <Input
              name='Password'
              type='password'
              value={userDetails.password}
              onChange={(e) => {
                setUserDetails((prevState) => {
                  return {
                    ...prevState,
                    password: e.target.value,
                  };
                });
                clearFieldErrors("password");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  signInHandler();
                }
              }}
              isInvalid={fieldErrors.password}
              className={classes.inputContainer}
              inputClassName={classes.input}
            />
            <LinkButton
              className={classes.forgotPassword}
              label={"Forgot Password?"}
              onClick={forgotPasswordHandler}
            />
            <PrimaryButton
              name='LOGIN'
              className={classes.loginBtn}
              onClick={signInHandler}
            />
          </div>
        </Card>
      </div>
    </>
  );
};

export default SignIn;
